// src/pages/Dashboard.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { ArrowLeft, RefreshCw, Home, LogOut, BarChart3, TrendingUp, CalendarClock, ChevronRight } from 'lucide-react';
import useAnalytics from '../hooks/useAnalytics';
import DashboardCards from '../components/dashboard/DashboardCards';
import ProgressCharts from '../components/dashboard/ProgressCharts';
import { getGameName, getGameIcon } from '../utils/gameNames';
import { getChildren } from '../utils/childrenHelper';
import { TRIAL_SIGNUP_ENABLED } from '../config/featureFlags';
import { getTrialTimezone } from '../utils/trialHelper';
import { isoToDisplayDate } from '../utils/timezoneUtils';

function Dashboard() {
  const [user, setUser] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [openTrials, setOpenTrials] = useState([]);
  const navigate = useNavigate();

  const { dashboardData, isLoading, error, getDashboardData } = useAnalytics();

      // Add this debug component temporarily (you can remove it later)
    const DebugGameNames = ({ dashboardData }) => {
      if (!dashboardData?.gamesProgress && !dashboardData?.recentSessions) return null;
      
      console.log('=== DEBUG GAME NAMES ===');
      
      if (dashboardData.gamesProgress) {
        dashboardData.gamesProgress.forEach(game => {
          console.log(`Game Type: "${game.gameType}" -> Mapped Name: "${getGameName(game.gameType)}"`);
        });
      }
      
      if (dashboardData.recentSessions) {
        dashboardData.recentSessions.slice(0, 3).forEach(session => {
          console.log(`Session Game Type: "${session.gameType}" -> Mapped Name: "${getGameName(session.gameType)}"`);
        });
      }
      
      return null; // This component just logs, doesn't render anything
    };


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        navigate("/login", { replace: true });
      } else {
        setUser(currentUser);
      }
      setIsAuthReady(true);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load dashboard data when component mounts
  // ✅ DEBUG: Log children data when available
// Load dashboard data when component mounts
  useEffect(() => {
    if (user) {
      getDashboardData();
    }
  }, [user, getDashboardData]);

  // ✅ DEBUG: Log children data when available (SEPARATE useEffect)
  useEffect(() => {
    const checkChildren = async () => {
      if (!user) return;
      
      try {
        const { getDoc, doc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('📊 Dashboard - Children Helper Check:', {
            childrenCount: getChildren(userData).length,
            children: getChildren(userData)
          });
        }
      } catch (error) {
        console.error('Error loading children data:', error);
      }
    };
    
    // Only run if we have user and dashboard data loaded
    if (user && dashboardData) {
      checkChildren();
    }
  }, [user, dashboardData]);

  // Open trial requests for this account (any signed-in user, incl. a paying
  // family that booked a trial for another child). Additive: the banner below
  // renders nothing when there are none. Feature-flagged.
  useEffect(() => {
    if (!TRIAL_SIGNUP_ENABLED || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'trials'), where('userId', '==', user.uid)));
        if (cancelled) return;
        const open = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((t) => t.status === 'requested' || t.status === 'scheduled');
        setOpenTrials(open);
      } catch (err) {
        console.error('Dashboard: failed to load trial requests', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("user");
      navigate("/login");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const getUserName = () => {
    if (user?.displayName) {
      return user.displayName.split(' ')[0];
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return "Friend";
  };

  const refreshData = () => {
    getDashboardData();
  };

  if (!isAuthReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-indigo-600 flex items-center justify-center">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full text-center">
          <div className="text-6xl mb-4 animate-spin">📊</div>
          <div className="text-purple-600 text-2xl font-bold animate-pulse">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  if (isLoading && !dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-indigo-600 flex items-center justify-center">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full text-center">
          <div className="text-6xl mb-4 animate-bounce">📈</div>
          <div className="text-purple-600 text-2xl font-bold animate-pulse">Loading your progress...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-indigo-600">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl mb-8 border-4 border-white/20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-4 shadow-lg">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">
                  Welcome back, {getUserName()}! 👋
                </h1>
                <p className="text-gray-600">Ready to see your amazing progress?</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={refreshData}
                disabled={isLoading}
                className="px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors flex items-center gap-2 font-semibold disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              
              <button
                onClick={() => navigate("/")}
                className="px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors flex items-center gap-2 font-semibold"
              >
                <Home className="w-4 h-4" />
                Games
              </button>

              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors flex items-center gap-2 font-semibold"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Open trial request banner — renders only when this account has one.
            Additive; links to the standalone /trial-status screen. */}
        {openTrials.length > 0 && (
          <button
            onClick={() => navigate('/trial-status')}
            className="w-full text-left bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-xl mb-8 border-4 border-purple-200 hover:border-purple-400 transition-colors flex items-center gap-4"
          >
            <div className="bg-purple-100 rounded-xl p-3 flex-shrink-0">
              <CalendarClock className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              {openTrials.length === 1 ? (
                <>
                  <div className="font-bold text-gray-800">
                    Free trial for {openTrials[0].studentName}
                  </div>
                  <div className="text-sm text-gray-600">
                    {openTrials[0].status === 'scheduled' && openTrials[0].scheduledAt
                      ? `Booked for ${isoToDisplayDate(openTrials[0].scheduledAt, getTrialTimezone(openTrials[0]) || 'UTC')}`
                      : "We've received your request — we'll be in touch to schedule it."}
                  </div>
                </>
              ) : (
                <>
                  <div className="font-bold text-gray-800">
                    You have {openTrials.length} trial requests
                  </div>
                  <div className="text-sm text-gray-600">Tap to see the details.</div>
                </>
              )}
            </div>
            <ChevronRight className="w-5 h-5 text-purple-400 flex-shrink-0" />
          </button>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-100 border-4 border-red-200 rounded-2xl p-6 mb-8">
            <div className="text-red-800 text-lg font-semibold">
              😕 Oops! Something went wrong loading your dashboard.
            </div>
            <div className="text-red-600 mt-2">
              {error.message || "Please try refreshing the page."}
            </div>
            <button
              onClick={refreshData}
              className="mt-4 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* No Data State */}
        {!isLoading && !error && (!dashboardData || dashboardData.sessionsCount === 0) && (
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl text-center">
            <div className="text-8xl mb-6">🎮</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Ready to Start Your Learning Adventure?
            </h2>
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              Your dashboard will show amazing insights about your learning journey!
            </p>
            <button 
              onClick={() => navigate("/")}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 mx-auto"
            >
              <Home size={20} />
              Go Play Games
            </button>
          </div>
        )}

<DebugGameNames dashboardData={dashboardData} />


        {/* Dashboard Content */}
        {!isLoading && !error && dashboardData && dashboardData.sessionsCount > 0 && (
          <div className="space-y-8">
            {/* Overview Cards */}
            <DashboardCards dashboardData={dashboardData} />
            
            {/* Progress Charts */}
            <ProgressCharts dashboardData={dashboardData} />
            
            {/* Recent Activity */}
            {dashboardData.recentSessions && dashboardData.recentSessions.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-xl border-4 border-orange-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-orange-100 rounded-xl p-3">
                    <TrendingUp className="w-6 h-6 text-orange-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800">Recent Activity</h2>
                </div>
                
                <div className="space-y-3">
                  {dashboardData.recentSessions.slice(0, 5).map((session, index) => {
                    const gameName = getGameName(session.gameType);
                    const gameIcon = getGameIcon(session.gameType);
                    const levelText = session.levelTitle || `Level ${session.level || 1}`;
                    
                    return (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border-2 border-gray-100">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center text-white font-bold">
                            {gameIcon}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-800">
                              {gameName} - {levelText}
                            </div>
                            <div className="text-sm text-gray-600">
                              {new Date(session.timestamp).toLocaleDateString()} • 
                              {Math.round((session.sessionDuration || 0) / 60)} min • 
                              {session.accuracy || 0}% accuracy
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-purple-600">
                            {session.totalScore?.toLocaleString() || 0} pts
                          </div>
                          <div className="text-yellow-500">
                            {'⭐'.repeat(session.starsEarned || 0)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Footer */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl text-center">
              <p className="text-gray-600">
                🎉 Keep up the amazing work! Every game you play helps you learn Arabic better! 
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Dashboard updates automatically as you play games
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;